/**
 * Reusable utility for computing radio button disabled/color states
 * based on buttonStateConfig and form-specific conditions
 */

import { ButtonStateConfig } from './buttonStateUtils'

export interface RadioButtonState {
  isDisabled: boolean
  labelColor: string
}

/**
 * Get disabled state based on buttonStateConfig permissions
 * Uses map lookup to reduce cognitive complexity of option matching
 */
const getButtonConfigDisabledState = (
  optionValue: string,
  buttonStateConfig: ButtonStateConfig
): boolean => {
  const disabledMap: Record<string, boolean> = {
    approved: buttonStateConfig.approvedDisabled,
    denied: buttonStateConfig.deniedDisabled,
    hold: buttonStateConfig.holdDisabled,
    returned: buttonStateConfig.returnedDisabled,
    partiallyapproved: buttonStateConfig.partiallyApprovedDisabled,
  }
  return disabledMap[optionValue] ?? false
}

/**
 * Get disabled state based on form-specific conditions
 * Only applies if not already disabled by buttonStateConfig
 */
const getFormConditionDisabledState = (
  optionValue: string,
  formConditions: {
    canHold?: boolean
    isRetracted?: boolean
    isPartiallyApprovedDisabled?: boolean
  }
): boolean => {
  // Disable Hold if cannot hold due to start date < 15 days
  if (optionValue === 'hold' && formConditions.canHold === false) {
    return true
  }
  // Disable Returned if this is a retraction request
  if (optionValue === 'returned' && formConditions.isRetracted === true) {
    return true
  }
  return false
}

/**
 * Compute radio button state (enabled/disabled + color) based on:
 * - buttonStateConfig permissions flags
 * - form-specific conditions (canHold, isRetracted, etc.)
 *
 * @param optionValue - Radio option value ('approved', 'denied', 'hold', 'returned', 'partiallyapproved')
 * @param buttonStateConfig - Button state configuration from getButtonStateConfig()
 * @param formConditions - Form-specific override conditions
 * @returns RadioButtonState with isDisabled and labelColor
 */
export const getRadioButtonState = (
  optionValue: string,
  buttonStateConfig: ButtonStateConfig | undefined,
  formConditions?: {
    canHold?: boolean
    isRetracted?: boolean
    isPartiallyApprovedDisabled?: boolean
  }
): RadioButtonState => {
  let isDisabled = false

  // Apply buttonStateConfig permissions-based disabled states
  if (buttonStateConfig) {
    isDisabled = getButtonConfigDisabledState(optionValue, buttonStateConfig)
  }

  // Apply form-specific condition overrides (only if not already disabled)
  if (formConditions && !isDisabled) {
    isDisabled = getFormConditionDisabledState(optionValue, formConditions)
  }

  const labelColor = isDisabled ? 'text-[#A0A5B6]' : 'text-[#0D1C3D]'

  return { isDisabled, labelColor }
}

/**
 * Check if partially approved option should be hidden
 * (e.g., for retraction flows or single-day requests)
 *
 * @param isRetracted - Whether this is a retraction request
 * @param numberOfDates - Number of dates in request
 * @returns true if option should be hidden
 */
export const shouldHidePartiallyApprovedOption = (
  isRetracted: boolean,
  numberOfDates: number
): boolean => {
  return isRetracted || numberOfDates <= 1
}
